import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { api } from '@/utils/api';
import { EyeIcon } from '@/ui/icons';

type Post = {
  id: string;
  title: string;
  slug: string;
  contentHtml: string;
  coverImageUrl?: string;
  publishedAt?: string;
  category?: { name: string; slug: string };
  viewCount?: number;
};

export function PostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const BASE_ORIGIN = 'https://esdfl.net';
  const siteOrigin = BASE_ORIGIN;
  const absolutize = (url?: string) => {
    if (!url) return url;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.startsWith('/')) return BASE_ORIGIN + url;
    return BASE_ORIGIN + '/' + url;
  };

  const meta = useMemo(() => {
    if (!post) return null;
    const plain = post.contentHtml.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    const desc = plain.slice(0, 160);
    return {
      title: `${post.title} - Edirne Süleyman Demirel Fen Lisesi | ESDFL`,
      description: desc,
      image: post.coverImageUrl,
    };
  }, [post]);

  const postUrl = useMemo(() => post ? (siteOrigin + '/yazi/' + post.slug) : '', [siteOrigin, post]);
  const jsonLdArticle = useMemo(() => {
    if (!post) return null;
    const data: any = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: post.title,
      datePublished: post.publishedAt,
      dateModified: post.publishedAt,
      url: postUrl,
      mainEntityOfPage: postUrl,
      author: { '@type': 'Organization', name: 'Edirne Süleyman Demirel Fen Lisesi' },
      publisher: {
        '@type': 'Organization',
        name: 'Edirne Süleyman Demirel Fen Lisesi',
        logo: { '@type': 'ImageObject', url: BASE_ORIGIN + '/esdfl.png' }
      }
    };
    if (post.coverImageUrl) data.image = absolutize(post.coverImageUrl);
    return data;
  }, [post, postUrl, siteOrigin]);
  const jsonLdBreadcrumb = useMemo(() => {
    if (!post) return null;
    const items: any[] = [
      { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: siteOrigin + '/' }
    ];
    if (post.category) {
      items.push({ '@type': 'ListItem', position: 2, name: post.category.name, item: siteOrigin + '/kategori/' + post.category.slug });
      items.push({ '@type': 'ListItem', position: 3, name: post.title, item: postUrl });
    } else {
      items.push({ '@type': 'ListItem', position: 2, name: post.title, item: postUrl });
    }
    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items
    };
  }, [post, postUrl, siteOrigin]);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await api.get(`/api/public/post/${slug}`);
        if (!ignore) setPost(res.post ?? null);
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    // Track per-post view
    (async () => {
      try { await api.post(`/api/public/post_view/${slug}`, {}); if (!ignore) setPost((p) => p ? { ...p, viewCount: (p.viewCount ?? 0) + 1 } : p); } catch {}
    })();
    return () => {
      ignore = true;
    };
  }, [slug]);

  if (loading) return <div>Yükleniyor…</div>;
  if (!post) return <div>Yazı bulunamadı.</div>;

  return (
    <article className="mx-auto max-w-3xl">
      {meta && (
        <Helmet>
          <title>{meta.title}</title>
          <meta name="description" content={meta.description} />
          <meta property="og:title" content={meta.title} />
          <meta property="og:description" content={meta.description} />
          <meta property="og:type" content="article" />
          {meta.image && <meta property="og:image" content={meta.image} />}
          <link rel="canonical" href={postUrl} />
          {jsonLdArticle && <script type="application/ld+json">{JSON.stringify(jsonLdArticle)}</script>}
          {jsonLdBreadcrumb && <script type="application/ld+json">{JSON.stringify(jsonLdBreadcrumb)}</script>}
        </Helmet>
      )}
      <header className="mb-6">
        {post.category && (
          <div className="text-sm text-brand-700">{post.category.name}</div>
        )}
        <h1 className="text-3xl font-serif font-bold mt-1">{post.title}</h1>
        <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
          {post.publishedAt && <span>{new Date(post.publishedAt).toLocaleDateString('tr-TR')}</span>}
          <span className="inline-flex items-center gap-1"><EyeIcon /> {post.viewCount ?? 0}</span>
        </div>
      </header>
      {post.coverImageUrl && (
        <img src={absolutize(post.coverImageUrl)} alt={post.title} className="rounded-lg mb-6 w-full object-cover" />
      )}
      <div className="tiptap" dangerouslySetInnerHTML={{ __html: post.contentHtml }} />
    </article>
  );
}


